import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, concat, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementRouteGateService {

  constructor(private router: Router) { }

  // Emits once, the first time the user is on an allowed route. An empty list means
  // no restriction: it emits immediately without subscribing to the router at all.
  firstAllowed$(allowedRoutes: string[]): Observable<boolean> {
    const routes = (allowedRoutes ?? []).filter(route => !!route?.trim()).map(route => this.normalise(route));
    if (!routes.length) return of(true);

    const navigated$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    );

    // Seeded with the current url: a user already on an allowed route may never
    // navigate again, and would otherwise never see the announcement.
    return concat(of(this.router.url), navigated$).pipe(
      filter(url => this.isAllowed(url, routes)),
      take(1),
      map(() => true)
    );
  }

  // Segment-aware, so a parent entry covers its children but '/ai' never
  // captures '/ai-readiness'.
  private isAllowed(url: string, routes: string[]): boolean {
    const path = this.normalise(url.split('?')[0].split('#')[0]);
    return routes.some(route => path === route || path.startsWith(route + '/'));
  }

  private normalise(value: string): string {
    const trimmed = value.trim().toLowerCase().replace(/\/+$/, '');
    return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
  }
}
